<template>
  <preloader v-if="isLoading"/>
  <zero-state
      v-else-if="showZeroState"
      :meta="zeroStateMeta"
      :background-transparent="false"
      style="margin-top: 30px"
      @open-create-replenishment-modal="openPayoutDepositModal({currencyId: null, networkId: null})"
  />
  <div v-else class="payout-wallet-balance">
    <Container :overflow="true" class="payout-wallet-balance__container-top">
      <div class="payout-wallet-balance__top-button-container">
          <orphan-search class="mobileHidden" :placeholder="$t('new_invoice.search')" v-model="searchString" />
          <base-button class="address-add-btn" type="primary" :disabled="!isCreateAddressAllowed" :label="$t('payout-wallet.add-address')" @click="openPayoutDepositModal({currencyId: null, networkId: null})">
              <template #prepend>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 3.33301V12.6663" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M3.3335 8H12.6668" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
              </template>
          </base-button>
          <div class="payout-wallet-balance__top-button-container--mobile">
              <refresh-button class="desktopHidden" @click="fetchBalances" />
              <filter-mobile :isFiltered="isFiltered" class="desktopHidden filters--mobile" @reset="resetFilters">
                  <orphan-search :placeholder="$t('new_invoice.search')" v-model="searchString" />
                  <business-wallet-network-select class="networkSelect" :value="networksValue" :list="filteredNetworks" uniq-key="id" :placeholder="$t('orders.select_network')" clearable  @input="handleSelectNetwork" />
                  <business-wallet-network-select :value="currencyValue" :list="filteredCurrencies" uniq-key="id" :placeholder="$t('orders.select_currencies')" clearable  @input="handleSelectCurrency" />
                  <address-risks-select :value="selectedRisk" @input="handleSelectRisk" />
              </filter-mobile>
          </div>
      </div>
      <div class="payout-wallet-balance__filter-container mobileHidden">
        <div class="payout-wallet-balance__select-container">
            <business-wallet-network-select class="networkSelect" :value="networksValue" :list="filteredNetworks" uniq-key="id" :placeholder="$t('orders.select_network')" clearable  @input="handleSelectNetwork" />
        </div>
        <div class="payout-wallet-balance__select-container">
            <business-wallet-network-select :value="currencyValue" :list="filteredCurrencies" uniq-key="id" :placeholder="$t('orders.select_currencies')" clearable  @input="handleSelectCurrency" />
        </div>
        <div class="payout-wallet-balance__select-container">
          <address-risks-select :value="selectedRisk" @input="handleSelectRisk" />
        </div>
          <refresh-button @click="fetchBalances" />
      </div>
        <div v-for="payoutBalance in paginateSortedBalances">
            <div
              class="payout-wallet-balance__wrap"
              :class="{ 'autoswap': payoutBalance.useForAutoSwap }"
            >
                <div class="payout-wallet-balance__row">
                  <p class="payout-wallet-balance__title-text">{{ $t('orders.alias') }}</p>

                  <div class="payout-wallet-balance__value">
                    <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100px">
                      {{ payoutBalance.alias }}
                    </div>
                  </div>
                </div>
                <div class="payout-wallet-balance__row">
                    <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.tab.currency-and-network') }}</p>

                    <p class="payout-wallet-balance__value">
                        <currency-and-network
                            :currency-id="payoutBalance.currencyId"
                            :network-id="payoutBalance.networkId"
                        />
                    </p>
                </div>

                <div class="payout-wallet-balance__row">
                    <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.balance.address') }}</p>
                    <div class="address">
                      <CopyString
                          :string="payoutBalance.address"
                          :successMessage="$t('copy.addresses_copied')"
                          :failMessage="$t('copy.addresses_not_copied')"
                          :title="$t('copy.notification')"
                          :placement="'left'"
                          canCopy
                      />
                    </div>
                </div>
              <div class="payout-wallet-balance__row">
                <p class="payout-wallet-balance__title-text">{{ $t('addressRisks.placeholder') }}</p>
                <div class="address">
                  <address-risks :status="payoutBalance.riskLevel" />
                </div>
              </div>
                <div class="payout-wallet-balance__row">
                    <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.balance.amount') }}</p>

                    <p class="payout-wallet-balance__value">{{ getFormattedBalance(payoutBalance.balance) }}</p>
                </div>
              <div class="payout-wallet-balance__row">
                <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.balance.gas') }}</p>

                <p class="payout-wallet-balance__value">
                  <svg width="16" class="gas-icon" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.6 14.4L11.488 6.616H8.8V1.6L4.8 9.384H7.6V14.4ZM8 0C10.2 0 12.08 0.8 13.64 2.36C15.2 3.92 16 5.8 16 8C16 10.2 15.2 12.08 13.64 13.64C12.08 15.2 10.2 16 8 16C5.8 16 3.92 15.2 2.36 13.64C0.8 12.08 0 10.2 0 8C0 5.8 0.8 3.92 2.36 2.36C3.92 0.8 5.8 0 8 0Z" fill="#44B070"/>
                  </svg>
                  {{ getFormattedBalance(payoutBalance.nativeBalance)  }} {{ nativeCurrencies[payoutBalance.networkId] ? nativeCurrencies[payoutBalance.networkId].currency : null }}
                </p>
              </div>
                <div style="display: flex; align-items: center; justify-content: end; gap: 5px; flex-wrap: nowrap">
                  <svg v-if="payoutBalance.useForAutoSwap" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.06053 2.29008C-0.000488281 3.75045 -0.000488281 5.83363 -0.000488281 10C-0.000488281 14.1664 -0.000488281 16.2496 1.06053 17.7099C1.40319 18.1816 1.81796 18.5963 2.28959 18.939C3.74996 20 5.83314 20 9.99951 20C14.1659 20 16.2491 20 17.7094 18.939C18.1811 18.5963 18.5958 18.1816 18.9385 17.7099C19.9995 16.2496 19.9995 14.1664 19.9995 10C19.9995 5.83363 19.9995 3.75045 18.9385 2.29008C18.5958 1.81844 18.1811 1.40368 17.7094 1.06102C16.2491 0 14.1659 0 9.99951 0C5.83314 0 3.74996 0 2.28959 1.06102C1.81796 1.40368 1.40319 1.81844 1.06053 2.29008ZM12.1773 4.33175C12.4916 4.0162 12.8755 3.71765 13.3865 3.63622C13.4914 3.61952 13.5972 3.61111 13.7033 3.61111C13.8094 3.61111 13.9153 3.61952 14.0201 3.63622C14.5312 3.71765 14.915 4.0162 15.2294 4.33175C15.5323 4.63591 15.8667 5.05765 16.2544 5.54664L17.3193 6.88966C17.6052 7.2503 17.5446 7.77445 17.184 8.06039C16.8234 8.34633 16.2992 8.28579 16.0133 7.92515L14.9778 6.61925C14.8094 6.40683 14.6645 6.22433 14.5366 6.06715V14.8148C14.5366 15.2751 14.1635 15.6482 13.7033 15.6482C13.2431 15.6482 12.87 15.2751 12.87 14.8148V6.06715C12.7422 6.22433 12.5972 6.40683 12.4288 6.61925L11.3933 7.92515C11.1074 8.28578 10.5832 8.34633 10.2226 8.06039C9.86197 7.77445 9.80142 7.2503 10.0874 6.88966L11.1522 5.54666C11.5399 5.05766 11.8743 4.63592 12.1773 4.33175ZM7.12914 5.185C7.12914 4.72476 6.75605 4.35167 6.29581 4.35167C5.83557 4.35167 5.46248 4.72476 5.46248 5.185L5.46248 13.9327C5.33466 13.7755 5.1897 13.593 5.02127 13.3806L3.98583 12.0747C3.69988 11.714 3.17573 11.6535 2.8151 11.9394C2.45447 12.2254 2.39392 12.7495 2.67986 13.1102L3.74473 14.4532C4.13241 14.9422 4.46677 15.3639 4.76976 15.6681C5.08411 15.9836 5.46796 16.2822 5.97901 16.3636C6.08386 16.3803 6.18972 16.3887 6.29581 16.3887C6.4019 16.3887 6.50775 16.3803 6.61261 16.3636C7.12366 16.2822 7.50752 15.9836 7.82186 15.6681C8.12484 15.3639 8.4592 14.9422 8.84687 14.4532L9.91175 13.1102C10.1977 12.7495 10.1371 12.2254 9.77652 11.9394C9.41588 11.6535 8.89173 11.714 8.60579 12.0747L7.57035 13.3806C7.40192 13.593 7.25696 13.7755 7.12914 13.9327L7.12914 5.185Z" fill="#744DB2"/>
                  </svg>
                  <actions-popup>
                    <div :class="['actions__modal-item', payoutBalance.balance <= 0 || !isWithdrawAllowed ? 'disabled' : '' ]" @click="openCreateWithdrawalRequestModal(payoutBalance.id)">
                      <svg width="14" height="15" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.2861 8.79999L12.2861 4M12.2861 4L7.28613 8.79998M12.2861 4L12.2861 16" :stroke="payoutBalance.balance > 0 || !isWithdrawAllowed ? 'var(--new-front-secondary-font-color' : 'var(--new-front-btn-bg-primary-disabled-text)'"
                              stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path
                            d="M2.94154 15.7867C3.47593 17.8466 4.68989 19.7275 6.48279 21.0754C9.94394 23.6773 14.7125 23.6533 18.1402 21.0166C19.8915 19.6695 21.073 17.8095 21.5925 15.7774"
                            :stroke="payoutBalance.balance > 0 || !isWithdrawAllowed ? 'var(--new-front-secondary-font-color' : 'var(--new-front-btn-bg-primary-disabled-text)'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <span>{{ $t('addresses.withdraw') }}</span>
                    </div>
                    <div class="actions__modal-item" @click="$router.push(`/history-address/${ payoutBalance.id }`)">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 1C5.46183 1.00439 3.98409 1.59935 2.872 2.662V1.6C2.872 1.44087 2.80879 1.28826 2.69626 1.17574C2.58374 1.06321 2.43113 1 2.272 1C2.11287 1 1.96026 1.06321 1.84774 1.17574C1.73521 1.28826 1.672 1.44087 1.672 1.6V4.3C1.672 4.45913 1.73521 4.61174 1.84774 4.72426C1.96026 4.83678 2.11287 4.9 2.272 4.9H4.972C5.13113 4.9 5.28374 4.83678 5.39626 4.72426C5.50878 4.61174 5.572 4.45913 5.572 4.3C5.572 4.14087 5.50878 3.98826 5.39626 3.87574C5.28374 3.76321 5.13113 3.7 4.972 3.7H3.532C4.30347 2.89381 5.3317 2.38159 6.43992 2.2514C7.54814 2.12121 8.66709 2.38118 9.60439 2.98663C10.5417 3.59207 11.2388 4.50515 11.5758 5.56887C11.9128 6.6326 11.8687 7.78051 11.451 8.81524C11.0334 9.84996 10.2683 10.7069 9.28729 11.2386C8.30629 11.7703 7.17069 11.9436 6.07573 11.7288C4.98077 11.5139 3.99488 10.9243 3.28755 10.0613C2.58023 9.19831 2.19567 8.11583 2.2 7C2.2 6.84087 2.13679 6.68826 2.02426 6.57573C1.91174 6.46321 1.75913 6.4 1.6 6.4C1.44087 6.4 1.28826 6.46321 1.17574 6.57573C1.06321 6.68826 1 6.84087 1 7C1 8.18669 1.35189 9.34672 2.01118 10.3334C2.67047 11.3201 3.60754 12.0891 4.7039 12.5433C5.80025 12.9974 7.00665 13.1162 8.17054 12.8847C9.33443 12.6532 10.4035 12.0818 11.2426 11.2426C12.0818 10.4035 12.6532 9.33443 12.8847 8.17054C13.1162 7.00665 12.9974 5.80025 12.5433 4.7039C12.0891 3.60754 11.3201 2.67047 10.3334 2.01118C9.34672 1.35189 8.18669 1 7 1ZM7 4.6C6.84087 4.6 6.68826 4.66321 6.57573 4.77574C6.46321 4.88826 6.4 5.04087 6.4 5.2V7C6.4 7.15913 6.46321 7.31174 6.57573 7.42426C6.68826 7.53678 6.84087 7.6 7 7.6H8.2C8.35913 7.6 8.51174 7.53678 8.62426 7.42426C8.73678 7.31174 8.8 7.15913 8.8 7C8.8 6.84087 8.73678 6.68826 8.62426 6.57573C8.51174 6.46321 8.35913 6.4 8.2 6.4H7.6V5.2C7.6 5.04087 7.53678 4.88826 7.42426 4.77574C7.31174 4.66321 7.15913 4.6 7 4.6Z" fill="var(--new-front-secondary-font-color)"/>
                      </svg>
                      <span>{{ $t('profits.history') }}</span>
                    </div>
                    <div class="actions__modal-item" @click="openInfoPayoutModal(payoutBalance)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M7 13.125C3.61726 13.125 0.875 10.3827 0.875 7C0.875 3.61726 3.61726 0.875 7 0.875C10.3827 0.875 13.125 3.61726 13.125 7C13.125 10.3827 10.3827 13.125 7 13.125ZM7 14C10.866 14 14 10.866 14 7C14 3.13401 10.866 0 7 0C3.13401 0 0 3.13401 0 7C0 10.866 3.13401 14 7 14Z" fill="#7E7E7E"/>
                        <path d="M7.81437 5.7644L5.80973 6.01562L5.73795 6.34888L6.13272 6.42065C6.38907 6.48218 6.44034 6.57446 6.38395 6.83081L5.73795 9.86597C5.56876 10.6504 5.83023 11.0195 6.44547 11.0195C6.92228 11.0195 7.47599 10.7991 7.72721 10.4966L7.80411 10.1326C7.6298 10.2864 7.37345 10.3479 7.20426 10.3479C6.96329 10.3479 6.87613 10.1787 6.93766 9.88135L7.81437 5.7644Z" fill="#7E7E7E"/>
                        <path d="M7.875 3.9375C7.875 4.42075 7.48325 4.8125 7 4.8125C6.51675 4.8125 6.125 4.42075 6.125 3.9375C6.125 3.45425 6.51675 3.0625 7 3.0625C7.48325 3.0625 7.875 3.45425 7.875 3.9375Z" fill="#7E7E7E"/>
                      </svg>
                      <span>{{ $t('payout-wallet.balance.menu.info') }}</span>
                    </div>
                  </actions-popup>
                </div>
            </div>
            <div
              class="payout-wallet-balance__wrap payout-wallet-balance__wrap_mobile"
              :class="{ 'autoswap': payoutBalance.useForAutoSwap }"
            >
                <div class="payout-wallet-balance__detailed-info">

                  <div class="payout-wallet-balance__row">
                    <p class="payout-wallet-balance__title-text">{{ $t('orders.alias') }}</p>

                    <div class="payout-wallet-balance__value">
                      <div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100px">
                        {{ payoutBalance.alias }}
                      </div>
                    </div>
                  </div>

                  <div class="payout-wallet-balance__row">
                    <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.tab.currency-and-network') }}</p>

                    <p class="payout-wallet-balance__value">
                      <currency-and-network
                        :currency-id="payoutBalance.currencyId"
                        :network-id="payoutBalance.networkId"
                      />
                    </p>
                  </div>

                    <div class="payout-wallet-balance__row">
                        <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.balance.address') }}</p>
                        <CopyString
                            :string="payoutBalance.address"
                            :successMessage="$t('copy.addresses_copied')"
                            :failMessage="$t('copy.addresses_not_copied')"
                            :title="$t('copy.notification')"
                            :placement="'left'"
                            canCopy
                        />
                    </div>
                  <div class="payout-wallet-balance__row">
                    <p class="payout-wallet-balance__title-text">{{ $t('addressRisks.placeholder') }}</p>
                    <address-risks :status="payoutBalance.riskLevel" style="width: unset" />
                  </div>
                    <div class="payout-wallet-balance__row">
                        <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.balance.amount') }}</p>
                        <p class="payout-wallet-balance__value">{{ getFormattedBalance(payoutBalance.balance) }}</p>
                    </div>
                  <div class="payout-wallet-balance__row">
                        <p class="payout-wallet-balance__title-text">{{ $t('payout-wallet.balance.gas') }}</p>
                    <p class="payout-wallet-balance__value">
                      <svg width="16" class="gas-icon" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7.6 14.4L11.488 6.616H8.8V1.6L4.8 9.384H7.6V14.4ZM8 0C10.2 0 12.08 0.8 13.64 2.36C15.2 3.92 16 5.8 16 8C16 10.2 15.2 12.08 13.64 13.64C12.08 15.2 10.2 16 8 16C5.8 16 3.92 15.2 2.36 13.64C0.8 12.08 0 10.2 0 8C0 5.8 0.8 3.92 2.36 2.36C3.92 0.8 5.8 0 8 0Z" fill="#44B070"/>
                      </svg>
                      {{ getFormattedBalance(payoutBalance.nativeBalance)  }} {{ nativeCurrencies[payoutBalance.networkId] ? nativeCurrencies[payoutBalance.networkId].currency : null }}
                    </p>
                    </div>

                    <div class="payout-wallet-balance__withdraw">
                        <base-button type="primary" :disabled="payoutBalance.balance <= 0 || !isWithdrawAllowed" :label="$t('addresses.withdraw')" @click="openCreateWithdrawalRequestModal(payoutBalance.id)" >
                            <template #prepend>
                                <svg width="19" height="20" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M17.2861 8.79999L12.2861 4M12.2861 4L7.28613 8.79998M12.2861 4L12.2861 16" :stroke="payoutBalance.balance > 0 ? '#FFFFFF' : 'var(--new-front-btn-bg-primary-disabled-text)'"
                                          stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                    <path
                                        d="M2.94154 15.7867C3.47593 17.8466 4.68989 19.7275 6.48279 21.0754C9.94394 23.6773 14.7125 23.6533 18.1402 21.0166C19.8915 19.6695 21.073 17.8095 21.5925 15.7774"
                                        :stroke="payoutBalance.balance > 0 ? '#FFFFFF' : 'var(--new-front-btn-bg-primary-disabled-text)'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </template>
                        </base-button>
                        <history-button class="history-btn" @click="$router.push(`/history-address/${ payoutBalance.id }`)" />
                        <info-button class="info-btn" @click="openInfoPayoutModal(payoutBalance)" />
                    </div>
                </div>
            </div>
        </div>
        <template v-if="paginateSortedBalances.length === 0">
            <EmptyTable/>
        </template>
        <pagination
          v-if="payoutBalancesCount > this.pageSize"
          :total="payoutBalancesCount"
          :page-size="pageSize"
          :current="page"
          @page-change="catchPagination"
        />
    </Container>
    <NotEnoughAdvanceBalance />
    <InfoPayoutAddress @update="fetchBalances" />
  </div>
</template>

<script>
  import Container from "@/public/components/common/uiKit/Container.vue";
  import pagination from "@/public/components/common/uiKit/Pagination.vue";
  import preloader from "/public/components/common/uiKit/spinner/index.vue";
  import CopyString from "../CopyString";
  import CurrencyImg from "../CurrencyImg";
  import btn from '../../common/btn';
  import {mapActions, mapGetters, mapState} from "vuex";
  import _ from "lodash";
  import {getCurrencyAliasById, getNetworkSystemName} from "../../../filters";
  import EmptyTable from "../EmptyTable";
  import NotEnoughAdvanceBalance from "../../modals/business-wallet/NotEnoughAdvanceBalance";
  import BaseButton from "@/public/components/common/uiKit/button/base/index.vue";
  import OrphanSearch from "@/public/components/elements/orphan-transactions/OrphanSearch.vue";
  import BusinessWalletNetworkSelect
    from "@/public/components/elements/business-walet/Select/businessWalletNetworkSelect.vue";
  import RefreshButton from "@/public/components/common/uiKit/button/refresh/index.vue";
  import Downloader from "@/public/components/elements/personal-addresses/Select/Downloader/index.vue";
  import NetworkImg from "@/public/components/common/uiKit/networkImg/index.vue";
  import FilterMobile from "@/public/components/elements/accounts/History/FilterToggler/index.vue";
  import ActionsPopup from "@/public/components/common/uiKit/actionsPopup/ActionsPopup.vue";
  import HistoryButton from "@/public/components/common/uiKit/button/history/index.vue";
  import InfoButton from "@/public/components/common/uiKit/button/info/index.vue";
  import BigNumber from "bignumber.js";
  import AddressRisksSelect from "@/public/components/elements/address-risks/Select/AddressRisksSelect.vue";
  import AddressRisks from "@/public/components/elements/address-risks/AddressRisks.vue";

  export default {
    name: "PayoutWalletBalance",
    data() {
        return {
            pageSize: 10,
            page: 1,
            isLoading: true,
            networksValue: null,
            currencyValue: null,
            searchString: '',
            selectedDownloadType: null,
            selectedRisk: '',
        };
    },
    components: {
      AddressRisks,
      AddressRisksSelect,
        HistoryButton,
        InfoButton,
        ActionsPopup,
        FilterMobile,
        NetworkImg,
        Downloader,
        RefreshButton,
        BusinessWalletNetworkSelect,
        OrphanSearch,
        BaseButton,
        NotEnoughAdvanceBalance,
        EmptyTable,
        Container,
        CopyString,
        CurrencyImg,
        pagination,
        preloader,
        btn,
        ZeroState: () => import(/* webpackChunkName: "public/pages/zeroState" */ '/public/pages/zeroState'),
        InfoPayoutAddress: () => import(/* webpackChunkName: "public/components/modals/payout-transfer/InfoPayoutAddress" */ '/public/components/modals/payout-transfer/InfoPayoutAddress'),
        CurrencyAndNetwork: () => import(/* webpackChunkName: "public/components/elements/personal-addresses/List/Items/CurrencyAndNetwork" */ '/public/components/elements/personal-addresses/List/Items/CurrencyAndNetwork')
    },
    created() {
        this.fetchBalances()
    },
    methods: {
        getCurrencyAliasById,
        getNetworkSystemName,
        ...mapActions('payoutBalances', ['fetchPayoutBalances']),
        ...mapActions('app', ['openModal']),
        fetchBalances () {
            this.catchPagination()

            this.fetchPayoutBalances().finally(() => {
                this.isLoading = false;
            });
        },
        openAddressAddModal () {
            this.openModal({
                name: 'PayoutModalDeposit',
            });
        },
        catchPagination(page = 1) {
            this.page = page;
        },
        openPayoutTransferModal() {
            if (!this.isWithdrawAllowed) {
                return
            }
            this.openModal({
                name: 'PayoutModalTransfer',
                payload: {withoutCurrency: true},
            });
        },
        openPayoutDepositModal({currencyId, networkId}) {
            this.openModal({
                name: 'PayoutModalDeposit',
                payload: {
                    currencyId,
                    networkId,
                },
            });
        },
        handleSelectNetwork(value) {
            this.page = 1;
            this.networksValue = value;
        },
        handleSelectCurrency(value) {
            this.page = 1;
            this.currencyValue = value;
        },

      handleSelectRisk(value) {
        this.page = 1;
        this.selectedRisk = value;
      },

        openCreateWithdrawalRequestModal(addressId) {
            const {
                openModal,
            } = this;

            if (!this.isWithdrawAllowed) {
                return;
            }

          if (!this.userOtpEnabled) {
            openModal({
              name: 'TwoFaRestriction',
            });
            return;
          }

            if (Number(this.accounts[0].balance) < 0) {
                openModal({
                    name: 'NotEnoughAdvanceBalance',
                    payload: {
                        advanceBalanceAmount: this.accounts[0].balance,
                    },
                });
                return;
            }

            openModal({
                name: 'CreateWithdrawalRequestModalPayout',
                payload: {
                    addressId,
                },
            });
        },
        openInfoPayoutModal(payoutAddress) {
          const {
            openModal,
          } = this;

          openModal({
            name: 'InfoPayoutAddress',
            payload: {
              payoutAddress
            },
          });
        },

        getFormattedBalance (value) {
            return new BigNumber(value).decimalPlaces(8).toFixed()
        },
        resetFilters() {
          const filters = ['networksValue', 'currencyValue', 'searchString']

          filters.forEach(filter => {
            this.$set(this, filter, '')
          })

          this.fetchBalances()
        }
    },
    watch: {
        selectedDownloadType(v) {
            const {
                $route,
                invoiceCurrency,
                paymentCurrency,
                paymentNetwork,
                search,
                dates,
                status,
                sortValue
            } = this;
            if (!v) {return}
            if (v.id === 'csv') {
                this.downloadCSV({
                    route: $route,
                    id: search || null,
                    status,
                    currencyId: invoiceCurrency?.id,
                    receivedCurrencyId: paymentCurrency?.id,
                    receivedNetworkId: paymentNetwork?.id,
                    from: dates ? dates[0] : null,
                    to: dates ? dates[1] : null,
                    sortDate: sortValue
                });
            } else {
                this.downloadXLSX({
                    route: $route,
                    id: search || null,
                    status,
                    currencyId: invoiceCurrency?.id,
                    receivedCurrencyId: paymentCurrency?.id,
                    receivedNetworkId: paymentNetwork?.id,
                    from: dates ? dates[0] : null,
                    to: dates ? dates[1] : null,
                    sortDate: sortValue
                });
            }
            this.selectedDownloadType = undefined
        },
    },
    computed: {
        ...mapState('payoutBalances', ['payoutBalances', 'registriesData']),
        ...mapGetters('app', [
            'getCurrencyNameById',
            'nativeCurrencies'
        ]),
        ...mapState('accounts', ['accounts']),
        ...mapState('app', ['networks', 'currencies']),
        ...mapState('user', {
        userOtpEnabled: state => state.user.otpToken,
      }),
        ...mapGetters('organizations', ['userRoleRules']),
        isWithdrawAllowed() {
            return this.userRoleRules['payout_balances'].entities.make_withdrawal;
        },
        isCreateAddressAllowed() {
            return this.userRoleRules['payout_balances'].entities.create_payout_balances;
        },
        isTransferAllowed() {
            return this.userRoleRules['wallets_payin'].entities.make_transfer;
        },
        filteredCurrencies () {
           return this.currencies.filter(({allowPayout}) => allowPayout)
        },
        isFiltered ({ networksValue,  currencyValue, searchString}) {
            return !!networksValue || !!currencyValue || !!searchString
        },
        filteredNetworks () {
          const networks = this.filteredCurrencies.map(({networks}) => networks)
          const flatArr = networks.flat()

          const currentNetworks = {}

          for(const network of flatArr) {
            if(!network.allowDeposit) {
              continue;
            }

            currentNetworks[network.id] = network;
          }

          return Object.values(currentNetworks);
            // const networks = this.filteredCurrencies.map(({networks}) => networks)
            // const flatArr = networks.flat()
            // // Массив уникальных сетей
            // return flatArr.filter((obj, index) =>
            //       flatArr.findIndex((item) => item.id === obj.id) === index && obj.allowDeposit
        },
        payoutBalanceFiltered() {
            const {
                payoutBalances,
            } = this;
            let preparedBalances = _.cloneDeep(payoutBalances);
            if (!this.currencyValue && this.networksValue) {
                preparedBalances = preparedBalances.filter((i) => i.networkId === this.networksValue.id);
            } else if (this.currencyValue && !this.networksValue) {
                preparedBalances = preparedBalances.filter((i) => i.currencyId === this.currencyValue.id);
            } else if (this.currencyValue && this.networksValue) {
                preparedBalances = preparedBalances.filter((i) => i.networkId === this.networksValue.id && i.currencyId === this.currencyValue.id);
            }
            if (this.searchString) {
              preparedBalances = preparedBalances.filter(({address}) => address.includes(this.searchString))
            }

            if(this.selectedRisk) {
              preparedBalances = preparedBalances.filter(({riskLevel}) => riskLevel === this.selectedRisk.value)
            }

            return preparedBalances;
        },
        paginateSortedBalances() {
          return this.payoutBalanceFiltered.slice((this.page - 1) * this.pageSize, this.page * this.pageSize);
        },
        payoutBalancesCount() {
            return this.payoutBalanceFiltered?.length ?? 0;
        },
        isEmptyFilters({ networksValue, currencyValue, searchString, selectedRisk }) {
          return !searchString && _.isEmpty(networksValue) && _.isEmpty(currencyValue) && _.isEmpty(selectedRisk)
        },
        showZeroState({ isEmptyFilters, paginateSortedBalances }) {
          return isEmptyFilters && !paginateSortedBalances.length
        },
        zeroStateMeta({ isCreateAddressAllowed }) {
          const meta = {
            title: this.$t('zero-states.pay-out.title'),
            subtitle: this.$t('zero-states.pay-out.subtitle'),
            notifications: [
              this.$t('zero-states.pay-out.notifications.first-line')
            ],
            actions: [
              {
                title: this.$t('zero-states.pay-out.actions.api-integration.title'),
                info: this.$t('zero-states.pay-out.actions.api-integration.info'),
                btnName: this.$t('zero-states.pay-out.actions.api-integration.btn-name'),
                btnSecondary: true,
                withoutIcon: true,
                apiMethod: '/api-reference/03addresses/post_payout-address'
              }
            ],
            type: 'default'
          }

          if (isCreateAddressAllowed) {
            meta.actions.unshift(
                {
                  title: this.$t('zero-states.pay-out.actions.create-replenishment.title'),
                  info: this.$t('zero-states.pay-out.actions.create-replenishment.info'),
                  btnName: this.$t('zero-states.pay-out.actions.create-replenishment.btn-name'),
                  action: 'open-create-replenishment-modal'
                }
            )
          }

          return meta
        }
    }
};
</script>

<style lang="scss" scoped>
.history-btn {
    min-width: 48px;
}
.gas-icon {
  margin-right: 5px;
  min-width: 16px;
}
.address-add-btn {
    width: 230px;
}
.desktopHidden {
  display: none;
}
.payout-wallet-balance {

  &__title-text {
    font-family: var(--new-front-font-family);
    font-style: normal;
    font-weight: 500;
    font-size: $h6;
    color: var(--new-front-text-secondary-bg-secondary);
    margin-bottom: 5px;
  }

  &__value {
    font-family: var(--new-front-font-family);
    font-style: normal;
    font-size:$h4;
    color: var(--new-front-table-text-primary-bg-secondary);
      font-weight: 500;
    display: flex;
    align-items: center;
  }

  &__row {
    .address {
      display: flex;
      align-items: center;
      min-height: 26px;
    }
  }

  &__label {
    font-family: var(--new-front-font-family);
    font-size: var(--font-size-text-main);
    line-height: var(--line-height);
    color: var(--primary-font-color);
  }

  &__select-container {
    width: 100%;
  }

  &__new-button {
    height: 46px;
    max-width: 211px;
    width: 100%;
    background: #0059F7;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    transition: all 0.3s ease-in;

    &:hover {
      background: var(--blue-400);
    }
  }

  &__container {
    margin-top: 10px;
    margin-bottom: 10px;

    font-weight: 500;
    gap: 10px;
    display: flex;
    flex-direction: column;

    &-top {
      padding: 30px 35px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
  }

  &__top-button-container {
      width: 100%;
      display: grid;
      grid-template-columns: 1fr 230px;
      gap: 10px;
      margin-bottom: 10px;
  }

  &__filter-container {
    width: 100%;
    display: grid;
      // TODO: добавить это, когда будут выгрузки в пэйаутах
      //grid-template-columns: 1fr 1fr 50px 50px;
    grid-template-columns: 1fr 1fr 1fr 50px ;
    gap: 10px;
    margin-bottom: 20px;
  }

  &__currency {
    &-container {
      display: flex;
      align-items: center;
    }

    &-image {
      height: 26px;
      width: 26px;
      margin-right: 16px;
    }
  }

  &__wrap {
    display: grid;
    grid-template-columns: 0.5fr 1fr 0.75fr 1fr 0.5fr 0.5fr 73px;
    padding: 10px 20px;

    border: 1px solid var(--new-front-table-border-bg-secondary);
    border-radius: 10px;

    &.autoswap {
      background-color: var(--new-front-bg-success-bg-secondary)
    }

    &_mobile {
      display: none;
    }
  }

  &__withdraw {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    > .base-btn {
      max-width: 180px;
    }
  }

  &__button-withdraw {
    min-height: 46px;
    width: 115px;
    border: 1px solid var(--border-color);
    background: var(--secondary-color);
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 5px;
    cursor: pointer;
    padding-right: 5px;
    color: var(--additional-font-color);

    p {
      color: var(--additional-font-color);
    }

    &:hover {
      border: 1px solid var(--blue-400);
    }
    &:disabled {
      cursor: unset;
      p {
        color: var(--secondary-font-color);
      }
      &:hover {
        border: 1px solid var(--border-color);
      }
    }
  }

  &__title {
    margin-bottom: 13px;
    font-family: var(--new-front-font-family);
    font-style: normal;
    font-weight: 400;
    font-size: var(--font-size-h1);
    line-height: var(--line-height);
    color: var(--primary-font-color);
  }

  &__subtitle {
    font-family: var(--new-front-font-family);
    font-style: normal;
    font-weight: 400;
    font-size: var(--font-size-h3);
    line-height: var(--line-height);
  }

  &__tab {
    width: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 66px;
    border-radius: 16px;


    &-underlay {
      display: flex;
      align-items: center;
      width: 100%;
      position: relative;
      min-height: 66px;
      border-radius: 16px;
      justify-content: flex-start;

      &_registry {
        justify-content: flex-end;
      }
    }

    &_right {
      position: absolute;
      right: 5px;
      width: calc(50% + 25px);
      padding-left: 25px;
      background: var(--grey-200);
      z-index: 1;
      border-top: 1px solid var(--border-color);
      border-right: 1px solid var(--border-color);
      border-bottom: 1px solid var(--border-color);
    }

    &_left {
      position: absolute;
      left: 0px;
      width: calc(50% + 25px);
      padding-left: 25px;
      background: var(--grey-200);
      z-index: 1;
      border-top: 1px solid var(--border-color);
      border-left: 1px solid var(--border-color);
      border-bottom: 1px solid var(--border-color);

    }


    &_left-active {
      position: relative;
      z-index: 2;
      background: var(--white-100);
      box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.04);
      border: none;
    }

    &_right-active {
      position: relative;
      z-index: 2;
      background: var(--white-100);
      box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.04);
      border: none;
    }
  }

  &__tab-container {
    min-height: 76px;
    padding: 10px;
    border-radius: 15px;
    background: var(--white-100);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }


  &__preview {
    width: 100%;
  }


  &__mobile {
    display: block;


  }

  &__registry-title {
    font-family: var(--new-front-font-family);
    font-size: var(--font-size-text-main);
    line-height: var(--line-height);
    color: var(--primary-font-color);
  }

  &__registry-status-text {
    font-family: var(--new-front-font-family);
    font-size: var(--font-size-text-main);
    line-height: var(--line-height);
    color: var(--primary-font-color);
  }

  &__registry-button-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding-top: 10px;
    padding-bottom: 10px;
    padding-right: 10px;
    gap: 10px;
  }

  &__registry-currency-image {
    width: 26px;
    height: 26px;
  }

  &__registry-image-container {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  &__registry-info-container {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    padding-left: 40px;
    padding-right: 40px;
    min-height: 55px;
    align-items: center;
    border-bottom: 1px solid var(--border-color);
  }
}
@media screen and (max-width: 1200px) {
  .payout-wallet-balance {
    &__withdraw {
      flex-direction: column;
      min-width: unset;
      align-items: flex-end;
    }
  }
}

@media screen and (max-width: 950px) {
  .payout-wallet-balance {
    &__value {
      font-size: $h6;

    }
    &__filter-container {
      flex-direction: column;
    }

    &__container-top {
      padding: 10px;
    }

    &__top-button-container {

      ::v-deep .button {
        justify-content: center;
        max-width: 190px;
        @media screen and (max-width: 950px) {
          min-width: 100%;
        }
      }
      ::v-deep .at-btn__icon {
        margin-right: 10px;
      }
    }

    &__select-container {
      max-width: 100%;
    }

    &__withdraw {
      flex-direction: row;
      justify-content: center;
      ::v-deep {
        >.base-btn {
          max-width: 100% !important;
        }
      }
    }

    &__button-withdraw {
      width: 100%;
      max-width: 115px;
    }

    &__currency {
      display: flex;
      align-items: center;
    }

    &__preview {
      display: flex;
      justify-content: space-between;
      flex: 1;
      align-items: center;
    }

    &__row {
      width: 100%;
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-height: 40px;
      //border-top: 1px solid var(--border-color);

      &:last-of-type {
        border-bottom: none;
        margin-bottom: 20px;
      }
    }

    &__wrap {
      display: none;

      &_mobile {
        display: block;
        padding: 10px;

      }
    }
  }
  .mobileHidden {
    display: none;
  }
    .desktopHidden {
        display: inherit;
    }
  ::v-deep {
    .copy-string {
      .copy-string__string {
        font-size: $h6;
      }
      .icon-wrapper {
        margin-right: 0;
      }
    }
  }
  .filters--mobile {
    &::v-deep {
      .currency-search-wrapper .search-input::placeholder {
        font-size: 13px !important;
        opacity: 1 !important;
      }
      .currency-search-wrapper {
        width: 100%;
      }
      .currency-search-wrapper .search-input {
        padding: 0 40px 0 10px;
      }
      .accounts-filter-by-date {
        width: 100%;
      }
    }
  }
}

@media screen and (max-width: 950px) {
  .payout-wallet-balance {
    &__top-button-container {
      grid-template-columns: 156px 1fr;
        justify-items: flex-end;
        padding: 10px;
        > .base-btn {
            max-width: 156px;
        }
        &--mobile {
            display: flex;
            align-items: center;
            gap: 10px;
            ::v-deep {
                .btn-wrapper {
                    width: 48px;
                }
            }
        }
    }
  }

  ::v-deep .currency-and-network-info-block {
    .currency-alias, .network-alias {
      font-size: $h6!important;
    }
  }
}
@media screen and (max-width: 576px) {
    ::v-deep {
        .container-new {
            padding: 10px;
        }
    }
  .balances__top-container {

  }
}

.currencyImg {
  margin-right: 15px;
  width: 30px;
  height: 30px;
}
::v-deep .at-select__dropdown .at-select__option {
  font-family: var(--new-front-font-family);
  font-size: var(--font-size-text-main);
  line-height: var(--line-height);
  color: var(--primary-font-color);

  padding: 10px;
  display: flex;
  align-items: center;

  &:hover {
    background: var(--hover-color-select);
  }
}
::v-deep {
  .search-input::placeholder {
    opacity: 1;
  }
  .advanced-balances-select-wrapper .sublabel {
    padding: 0;
  }
  .file-downloader {
    width: 48px;
  }
  .filter-toggler__content {
    gap: 10px;
    width: 300px;
  }
  .advanced-balances-select-wrapper {
    width: 100%;
  }
}
.secondary-text {
    color: var(--new-front-secondary-font-color)
}

.networkSelect {
    ::v-deep .currency-network-icon-wrapper .currency-network-icon__network {
        transform: translate(0%,-45%) !important;
    }
}
.disabled {
    opacity: .6;
    pointer-events: none;
    cursor: not-allowed;
}
.actions__modal-item {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 100px;
    width: 100%;
    border-bottom: 1px solid var(--new-front-input-border);
    color: var(--new-front-secondary-font-color);
    font-weight: 500;
    padding: $p10;
    transition: all .2s ease;
    cursor: pointer;
    &:hover {
        color:var(--new-front-primary-font-color) ;
        background: var(--new-font-nav-hover-bg);
    }
    &:first-of-type {
        &:hover {
            &::v-deep svg {
                path {
                    stroke: #744DB2 !important;
                }
            }
        }
    }
    &:nth-child(2), &:nth-child(3) {
      &:hover {
        &::v-deep svg {
          path {
            fill: #744DB2 !important;
          }
        }
      }
    }

    &:nth-child(3) {
      border-bottom: none;
    }
}
</style>
